/* Product detail page behaviour: gallery carousel, sticky add-to-cart, size guide. */

if (!customElements.get('pdp-gallery')) {
  customElements.define(
    'pdp-gallery',
    class PdpGallery extends HTMLElement {
      static ZOOM = 2;

      connectedCallback() {
        this.viewport = this.querySelector('[data-pdp-viewport]');
        if (!this.viewport) return;

        this.slides = Array.from(this.querySelectorAll('[data-pdp-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-pdp-dot]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-pdp-thumb]'));
        this.thumbsTrack = this.querySelector('[data-pdp-thumbs-track]');
        this.thumbsPrev = this.querySelector('[data-pdp-thumbs-prev]');
        this.thumbsNext = this.querySelector('[data-pdp-thumbs-next]');
        this.activeIndex = 0;

        this.onScroll = this.throttle(this.syncFromScroll.bind(this));
        this.viewport.addEventListener('scroll', this.onScroll, { passive: true });

        [...this.dots, ...this.thumbs].forEach((control) => {
          control.addEventListener('click', () => this.goTo(Number(control.dataset.index)));
        });

        this.viewport.addEventListener('keydown', this.onKeydown.bind(this));
        this.setupZoom();

        if (this.thumbsTrack) {
          this.onThumbsScroll = this.throttle(this.updateThumbsNav.bind(this));
          this.thumbsTrack.addEventListener('scroll', this.onThumbsScroll, { passive: true });
          this.thumbsPrev?.addEventListener('click', () => this.scrollThumbs(-1));
          this.thumbsNext?.addEventListener('click', () => this.scrollThumbs(1));
          this.thumbsResizeObserver = new ResizeObserver(() => this.updateThumbsNav());
          this.thumbsResizeObserver.observe(this.thumbsTrack);
          window.addEventListener('resize', this.onThumbsScroll, { passive: true });
        }

        this.variantUnsubscriber = window.subscribe?.(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId !== this.dataset.section) return;
          this.goToMedia(data.variant?.featured_media?.id);
        });

        this.setActive(0);
        this.updateThumbsNav();
      }

      disconnectedCallback() {
        this.viewport?.removeEventListener('scroll', this.onScroll);
        this.thumbsTrack?.removeEventListener('scroll', this.onThumbsScroll);
        window.removeEventListener('resize', this.onThumbsScroll);
        this.thumbsResizeObserver?.disconnect();
        this.variantUnsubscriber?.();
        this.closeLightbox();
      }

      /* Image zoom, as the old theme had it through Magic Zoom: on desktop a click on the
         photo shows a lens over it and the enlarged area in a window beside the gallery;
         on touch screens a tap opens the full-screen viewer. */
      setupZoom() {
        this.zoomSlides = this.slides.filter((slide) => slide.dataset.pdpZoom);
        if (!this.zoomSlides.length) return;

        this.lightbox = this.querySelector('[data-pdp-lightbox]');
        this.lightboxImages = Array.from(this.querySelectorAll('.pdp__lightbox-image'));
        this.zoomWindow = this.querySelector('[data-pdp-zoom-window]');
        this.canMagnify = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');

        this.zoomSlides.forEach((slide) => {
          slide.addEventListener('mousemove', (event) => {
            if (this.zoomSlide === slide) this.moveZoom(event);
          });
          slide.addEventListener('mouseleave', () => this.stopZoom());
          slide.addEventListener('click', (event) => {
            if (!this.canMagnify.matches) {
              this.openLightbox(this.zoomSlides.indexOf(slide));
            } else if (this.zoomSlide === slide) {
              this.stopZoom();
            } else {
              this.startZoom(slide, event);
            }
          });
        });

        this.lightbox?.addEventListener('click', (event) => {
          if (event.target.closest('[data-pdp-lightbox-close]')) this.closeLightbox();
        });
        this.onLightboxKeydown = (event) => {
          if (event.key === 'Escape') this.closeLightbox();
        };
      }

      startZoom(slide, event) {
        const image = slide.querySelector('img');
        if (!this.zoomWindow || !image?.naturalWidth) return;

        this.zoomSlide = slide;
        this.zoomImage = image;
        this.zoomLens = this.zoomLens || Object.assign(document.createElement('div'), { className: 'pdp__zoom-lens' });
        this.zoomLens.setAttribute('aria-hidden', 'true');
        slide.append(this.zoomLens);

        // Start from the photo already on screen, then swap in the large file once it is in.
        const source = slide.dataset.pdpZoom;
        this.zoomWindow.style.backgroundImage = `url('${image.currentSrc || image.src}')`;
        const preload = new Image();
        preload.onload = () => {
          if (this.zoomSlide === slide) this.zoomWindow.style.backgroundImage = `url('${source}')`;
        };
        preload.src = source;

        this.zoomWindow.hidden = false;
        this.moveZoom(event);
      }

      moveZoom(event) {
        const slide = this.zoomSlide;
        const image = this.zoomImage;
        const frame = slide.getBoundingClientRect();

        // Where the photo is actually drawn inside the frame (object-fit can leave bars or crop).
        const fit = getComputedStyle(image).objectFit === 'cover' ? Math.max : Math.min;
        const scale = fit(frame.width / image.naturalWidth, frame.height / image.naturalHeight);
        const drawnWidth = image.naturalWidth * scale;
        const drawnHeight = image.naturalHeight * scale;
        const drawnLeft = (frame.width - drawnWidth) / 2;
        const drawnTop = (frame.height - drawnHeight) / 2;

        const zoom = PdpGallery.ZOOM;
        const windowWidth = this.zoomWindow.clientWidth;
        const windowHeight = this.zoomWindow.clientHeight;
        const lensWidth = Math.min(windowWidth / zoom, drawnWidth);
        const lensHeight = Math.min(windowHeight / zoom, drawnHeight);

        const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
        const minLeft = Math.max(drawnLeft, 0);
        const minTop = Math.max(drawnTop, 0);
        const lensLeft = clamp(
          event.clientX - frame.left - lensWidth / 2,
          minLeft,
          Math.min(drawnLeft + drawnWidth, frame.width) - lensWidth
        );
        const lensTop = clamp(
          event.clientY - frame.top - lensHeight / 2,
          minTop,
          Math.min(drawnTop + drawnHeight, frame.height) - lensHeight
        );

        Object.assign(this.zoomLens.style, {
          width: `${lensWidth}px`,
          height: `${lensHeight}px`,
          transform: `translate(${lensLeft}px, ${lensTop}px)`,
        });

        this.zoomWindow.style.backgroundSize = `${drawnWidth * zoom}px ${drawnHeight * zoom}px`;
        this.zoomWindow.style.backgroundPosition = `${-(lensLeft - drawnLeft) * zoom}px ${-(lensTop - drawnTop) * zoom}px`;
      }

      stopZoom() {
        this.zoomSlide = null;
        this.zoomLens?.remove();
        if (this.zoomWindow) this.zoomWindow.hidden = true;
      }

      openLightbox(index) {
        if (!this.lightbox) return;
        this.lightboxOpener = document.activeElement;
        this.lightbox.hidden = false;
        document.body.classList.add('overflow-hidden');
        document.addEventListener('keydown', this.onLightboxKeydown);
        this.lightboxImages.forEach((image) => image.setAttribute('loading', 'eager'));
        this.lightboxImages[index]?.scrollIntoView({ block: 'start' });
        this.lightbox.querySelector('[data-pdp-lightbox-close]')?.focus({ preventScroll: true });
      }

      closeLightbox() {
        if (!this.lightbox || this.lightbox.hidden) return;
        this.lightbox.hidden = true;
        document.body.classList.remove('overflow-hidden');
        document.removeEventListener('keydown', this.onLightboxKeydown);
        this.lightboxOpener?.focus?.({ preventScroll: true });
      }

      throttle(callback) {
        let frame = null;
        return () => {
          if (frame) return;
          frame = requestAnimationFrame(() => {
            frame = null;
            callback();
          });
        };
      }

      syncFromScroll() {
        const { scrollLeft, clientWidth } = this.viewport;
        const index = Math.round(Math.abs(scrollLeft) / clientWidth);
        this.setActive(Math.min(Math.max(index, 0), this.slides.length - 1));
      }

      goTo(index) {
        if (Number.isNaN(index) || !this.slides[index]) return;
        // In an RTL scroller the start is the right edge and scrollLeft runs negative.
        const rtl = getComputedStyle(this.viewport).direction === 'rtl';
        const offset = index * this.viewport.clientWidth * (rtl ? -1 : 1);
        this.viewport.scrollTo({ left: offset, behavior: 'smooth' });
        this.setActive(index);
      }

      goToMedia(mediaId) {
        if (!mediaId) return;
        const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (index > -1) this.goTo(index);
      }

      onKeydown(event) {
        if (event.key === 'Enter' && this.slides[this.activeIndex]?.dataset.pdpZoom) {
          event.preventDefault();
          this.openLightbox(this.zoomSlides.indexOf(this.slides[this.activeIndex]));
          return;
        }
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        const forward = document.dir === 'rtl' ? event.key === 'ArrowLeft' : event.key === 'ArrowRight';
        event.preventDefault();
        this.goTo(this.activeIndex + (forward ? 1 : -1));
      }

      setActive(index) {
        this.activeIndex = index;
        this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
        this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
        this.ensureThumbVisible(this.thumbs[index]);
      }

      isThumbsVertical() {
        if (!this.thumbsTrack) return false;
        return this.thumbsTrack.scrollHeight > this.thumbsTrack.clientHeight + 1;
      }

      scrollThumbs(direction) {
        if (!this.thumbsTrack) return;
        const vertical = this.isThumbsVertical();
        const amount = (vertical ? this.thumbsTrack.clientHeight : this.thumbsTrack.clientWidth) * 0.75;
        const rtl = !vertical && getComputedStyle(this.thumbsTrack).direction === 'rtl';
        const delta = amount * direction * (rtl ? -1 : 1);
        this.thumbsTrack.scrollBy({
          top: vertical ? delta : 0,
          left: vertical ? 0 : delta,
          behavior: 'smooth',
        });
      }

      updateThumbsNav() {
        if (!this.thumbsTrack) return;
        const vertical = this.isThumbsVertical();
        const overflow = vertical
          ? this.thumbsTrack.scrollHeight > this.thumbsTrack.clientHeight + 1
          : this.thumbsTrack.scrollWidth > this.thumbsTrack.clientWidth + 1;

        [this.thumbsPrev, this.thumbsNext].forEach((button) => {
          if (!button) return;
          button.hidden = !overflow;
        });
        if (!overflow) return;

        const rtl = !vertical && getComputedStyle(this.thumbsTrack).direction === 'rtl';
        const max = vertical
          ? this.thumbsTrack.scrollHeight - this.thumbsTrack.clientHeight
          : this.thumbsTrack.scrollWidth - this.thumbsTrack.clientWidth;
        const pos = vertical
          ? this.thumbsTrack.scrollTop
          : Math.abs(this.thumbsTrack.scrollLeft);
        const atStart = pos <= 1;
        const atEnd = pos >= max - 1;

        if (this.thumbsPrev) this.thumbsPrev.disabled = rtl ? atEnd : atStart;
        if (this.thumbsNext) this.thumbsNext.disabled = rtl ? atStart : atEnd;
      }

      ensureThumbVisible(thumb) {
        if (!thumb || !this.thumbsTrack) return;
        const trackRect = this.thumbsTrack.getBoundingClientRect();
        const thumbRect = thumb.getBoundingClientRect();
        const vertical = this.isThumbsVertical();

        if (vertical) {
          if (thumbRect.top < trackRect.top) {
            this.thumbsTrack.scrollBy({ top: thumbRect.top - trackRect.top, behavior: 'smooth' });
          } else if (thumbRect.bottom > trackRect.bottom) {
            this.thumbsTrack.scrollBy({ top: thumbRect.bottom - trackRect.bottom, behavior: 'smooth' });
          }
          return;
        }

        if (thumbRect.left < trackRect.left) {
          this.thumbsTrack.scrollBy({ left: thumbRect.left - trackRect.left, behavior: 'smooth' });
        } else if (thumbRect.right > trackRect.right) {
          this.thumbsTrack.scrollBy({ left: thumbRect.right - trackRect.right, behavior: 'smooth' });
        }
      }
    }
  );
}

if (!customElements.get('pdp-sticky-atc')) {
  customElements.define(
    'pdp-sticky-atc',
    class PdpStickyAtc extends HTMLElement {
      connectedCallback() {
        this.bar = this.querySelector('[data-pdp-sticky-bar]');
        this.proxy = this.querySelector('[data-pdp-sticky-button]');
        this.proxyLabel = this.proxy?.querySelector('[data-pdp-sticky-label]');
        this.priceTarget = this.querySelector('[data-pdp-sticky-price]');

        const scope = this.closest('product-info') || document;
        this.source = scope.querySelector('[data-pdp-main-cta]');
        this.anchor = scope.querySelector('[data-pdp-buy]');
        this.sourcePrice = scope.querySelector('[data-pdp-price]');
        if (!this.bar || !this.source || !this.anchor) return;

        this.proxy?.addEventListener('click', () => {
          if (this.proxy.hasAttribute('disabled')) return;
          this.source.click();
        });

        // Driven by scroll position rather than IntersectionObserver: the observer
        // missed crossings on the live page, leaving the bar permanently hidden.
        this.onScroll = this.rafThrottle(this.updateVisibility.bind(this));
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.updateVisibility();

        this.mutationObserver = new MutationObserver(this.sync.bind(this));
        this.mutationObserver.observe(this.source, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
        });

        if (this.sourcePrice) {
          this.priceObserver = new MutationObserver(this.syncPrice.bind(this));
          this.priceObserver.observe(this.sourcePrice, { childList: true, subtree: true, characterData: true });
        }

        this.sync();
        this.syncPrice();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        this.mutationObserver?.disconnect();
        this.priceObserver?.disconnect();
      }

      rafThrottle(callback) {
        let frame = null;
        return () => {
          if (frame) return;
          frame = requestAnimationFrame(() => {
            frame = null;
            callback();
          });
        };
      }

      updateVisibility() {
        // Reveal once the main buy button has scrolled above the viewport.
        const passed = this.anchor.getBoundingClientRect().bottom < 0;
        this.bar.classList.toggle('is-visible', passed);
      }

      sync() {
        if (!this.proxy) return;
        const disabled = this.source.hasAttribute('disabled') || this.source.getAttribute('aria-disabled') === 'true';
        this.proxy.toggleAttribute('disabled', disabled);
        this.proxy.classList.toggle('loading', this.source.classList.contains('loading'));

        const label = this.source.querySelector('span:not(.visually-hidden)')?.textContent.trim();
        if (label && this.proxyLabel) this.proxyLabel.textContent = label;
      }

      syncPrice() {
        if (!this.priceTarget || !this.sourcePrice) return;
        this.priceTarget.innerHTML = this.sourcePrice.innerHTML;
      }
    }
  );
}

if (!customElements.get('pdp-size-guide')) {
  customElements.define(
    'pdp-size-guide',
    class PdpSizeGuide extends HTMLElement {
      connectedCallback() {
        this.modal = this.querySelector('[data-pdp-modal]');
        if (!this.modal) return;

        this.onKeydown = (event) => {
          if (event.key === 'Escape') this.close();
        };

        const scope = this.closest('product-info') || document;
        scope.querySelectorAll('[data-pdp-size-guide-open]').forEach((trigger) => {
          trigger.addEventListener('click', (event) => {
            event.preventDefault();
            this.open(trigger);
          });
        });

        this.modal.addEventListener('click', (event) => {
          if (event.target === this.modal || event.target.closest('[data-pdp-modal-close]')) this.close();
        });
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onKeydown);
        document.body.classList.remove('overflow-hidden');
      }

      open(trigger) {
        this.opener = trigger;
        this.modal.hidden = false;
        document.body.classList.add('overflow-hidden');
        document.addEventListener('keydown', this.onKeydown);
        this.modal.querySelector('[data-pdp-modal-close]')?.focus();
      }

      close() {
        this.modal.hidden = true;
        document.body.classList.remove('overflow-hidden');
        document.removeEventListener('keydown', this.onKeydown);
        this.opener?.focus();
      }
    }
  );
}

/* Wishlist heart over the gallery. The wishlist app renders its button as an app block in
   the info column, so move that block into the overlay from the Figma instead of asking the
   merchant for a snippet. Falls back to whatever is pasted in the section settings. */
if (!customElements.get('pdp-wishlist')) {
  customElements.define(
    'pdp-wishlist',
    class PdpWishlist extends HTMLElement {
      connectedCallback() {
        this.selector = (this.dataset.appSelector || '').trim();
        if (!this.selector) return;

        this.scope = this.closest('product-info') || document;
        if (this.adopt()) return;

        // App blocks can also be injected after this script runs.
        this.observer = new MutationObserver(() => {
          if (this.adopt()) this.observer.disconnect();
        });
        this.observer.observe(this.scope, { childList: true, subtree: true });
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      adopt() {
        let button = null;
        try {
          button = this.scope.querySelector(this.selector);
        } catch (error) {
          return true; // An invalid selector never matches — stop looking.
        }
        if (!button) return false;
        if (this.contains(button)) return true;

        // Move the whole app block so the app keeps the markup and styles it rendered.
        const node = button.closest('.shopify-app-block') || button;
        const host = node.parentElement;
        this.appendChild(node);

        if (host?.classList.contains('pdp__app') && !host.querySelector('*')) host.remove();
        const slot = this.scope.querySelector('[data-pdp-apps]');
        if (slot && !slot.querySelector('*')) slot.hidden = true;

        return true;
      }
    }
  );
}

/* App blocks are whatever the merchant installed, and several of them (size chart, analytics)
   render nothing in the column but still take a row in it, which leaves a stray gap under the
   add to cart button. Collapse the blocks that draw nothing, and the slot when none of them do. */
if (!customElements.get('pdp-apps')) {
  customElements.define(
    'pdp-apps',
    class PdpApps extends HTMLElement {
      connectedCallback() {
        // Apps mount after this script runs and can come and go later — the back in stock
        // button only appears on a sold out variant — so keep watching the markup.
        this.observer = new MutationObserver(() => this.sync());
        this.observer.observe(this, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'style', 'hidden'],
        });

        this.sync();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      sync() {
        const blocks = Array.from(this.children);

        // Measured visible, otherwise a block that was hidden once could never come back:
        // inside a hidden slot every app block measures zero whatever it has rendered.
        this.hidden = false;
        blocks.forEach((block) => {
          block.hidden = false;
        });

        const rendered = blocks.filter((block) => block.getBoundingClientRect().height > 0);
        blocks.forEach((block) => {
          block.hidden = !rendered.includes(block);
        });
        this.hidden = rendered.length === 0;

        // The writes above are mutations of our own; drop the records they just queued so the
        // observer only ever wakes up for an app changing what it renders.
        this.observer.takeRecords();
      }
    }
  );
}

/* "Complete the look" fallback. Shopify only exposes the recommendations object to a section
   rendered through the recommendations URL, so fetch this section that way and keep the
   carousel it rendered. */
if (!customElements.get('pdp-recommendations')) {
  customElements.define(
    'pdp-recommendations',
    class PdpRecommendations extends HTMLElement {
      connectedCallback() {
        const load = () => {
          this.observer?.disconnect();
          this.load();
        };

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (entries.some((entry) => entry.isIntersecting)) load();
            },
            { rootMargin: '0px 0px 400px 0px' }
          );
          this.observer.observe(this);
        } else {
          load();
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      load() {
        fetch(this.dataset.url)
          .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
          .then((text) => {
            const carousel = new DOMParser().parseFromString(text, 'text/html').querySelector('.pdp__ctl');
            if (carousel) this.replaceWith(carousel);
          })
          .catch(() => {
            // No recommendations for this product — leave the page without the section.
          });
      }
    }
  );
}

/* Accordions. <details> snaps open with no in-between state, so the summary click is taken
   over and the panel height is animated instead. The native `open` attribute still drives
   everything — it is set before the opening animation and cleared after the closing one —
   so keyboard, find-in-page and a no-JS page all keep working. */
if (!customElements.get('pdp-tabs')) {
  customElements.define(
    'pdp-tabs',
    class PdpTabs extends HTMLElement {
      connectedCallback() {
        this.querySelectorAll('details').forEach((details) => {
          const summary = details.querySelector('summary');
          const content = details.querySelector('[data-pdp-tab-content]');
          if (!summary || !content) return;

          summary.addEventListener('click', (event) => this.toggle(event, details, content));
        });
      }

      toggle(event, details, content) {
        // Without the API, or when motion is turned down, let <details> toggle itself.
        if (!content.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        event.preventDefault();

        const closing = details.open;
        // The panel is border-box, so a height below its bottom padding is clamped to that
        // padding and the last 20px would snap. Padding animates with the height instead —
        // read at rest, since mid-animation it would report an interpolated value.
        if (!details.animation) details.padding = getComputedStyle(content).paddingBottom;

        // Measured before the running animation is cancelled, so a tab reversed mid-flight
        // carries on from the height it is actually at.
        const from = closing ? content.getBoundingClientRect().height : 0;
        details.animation?.cancel();

        // Closing has to animate too, so the panel stays open until the animation is done.
        details.open = true;
        const to = closing ? 0 : content.scrollHeight;

        content.style.overflow = 'hidden';
        details.animation = content.animate(
          {
            height: [`${from}px`, `${to}px`],
            paddingBottom: closing ? [details.padding, '0px'] : ['0px', details.padding],
          },
          { duration: 250, easing: 'ease' }
        );

        details.animation.onfinish = () => {
          details.open = !closing;
          content.style.overflow = '';
          details.animation = null;
        };
      }
    }
  );
}
